import { Box, IconButton, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { BLOCK_WIDTH, getBranchOutputRatio } from './flowGeometry';

const portSx = (color, edge, ratio = 0.5) => ({
  position: 'absolute',
  [edge]: -7,
  left: `${ratio * 100}%`,
  ml: '-7px',
  width: 14,
  height: 14,
  borderRadius: '50%',
  bgcolor: 'background.paper',
  border: `2.5px solid ${color}`,
  cursor: 'crosshair',
  zIndex: 2,
  '&:hover': { transform: 'scale(1.25)' },
  transition: 'transform 100ms',
});

// A single absolutely-positioned block card on the canvas with its
// input/output connection ports, hover delete button and config summary.
const FlowBlockCard = ({
  block,
  def,
  summary,
  selected,
  readOnly,
  registerEl,
  onPointerDown,
  onOutputPointerDown,
  onInputPointerDown,
  onDelete,
}) => {
  const Icon = def.Icon;
  return (
    <Box
      ref={element => registerEl(block.blockId, element)}
      data-block-id={block.blockId}
      onPointerDown={event => onPointerDown(event, block)}
      sx={{
        position: 'absolute',
        left: block.position.x,
        top: block.position.y,
        width: BLOCK_WIDTH,
        boxSizing: 'border-box',
        bgcolor: 'background.paper',
        borderRadius: 2,
        borderLeft: `6px solid ${def.color}`,
        boxShadow: selected ? 8 : 2,
        outline: theme => (selected ? `2px solid ${theme.palette.primary.main}` : 'none'),
        p: 1.25,
        cursor: readOnly ? 'default' : 'grab',
        userSelect: 'none',
        touchAction: 'none',
        '&:hover .fz-block-delete': { opacity: 1 },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 1.5,
            bgcolor: alpha(def.color, 0.15),
            color: def.color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon fontSize='small' />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant='subtitle2' sx={{ lineHeight: 1.25 }}>
            {def.title}
          </Typography>
          <Typography
            variant='caption'
            color='text.secondary'
            noWrap
            title={summary}
            sx={{ display: 'block' }}
          >
            {summary}
          </Typography>
        </Box>
      </Box>
      {!readOnly && (
        <IconButton
          className='fz-block-delete'
          size='small'
          aria-label='Block löschen'
          onPointerDown={event => event.stopPropagation()}
          onClick={event => {
            event.stopPropagation();
            onDelete(block.blockId);
          }}
          sx={{ position: 'absolute', top: 2, right: 2, opacity: 0, transition: 'opacity 120ms' }}
        >
          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
        </IconButton>
      )}
      {!def.isStart && (
        <Box
          data-port='input'
          data-block-id={block.blockId}
          onPointerDown={event => onInputPointerDown(event, block)}
          sx={portSx(def.color, 'top')}
        />
      )}
      {def.branches ? (
        def.branches.map(entry => {
          const ratio = getBranchOutputRatio(entry.branch);
          return (
            <Box key={entry.branch}>
              <Box
                data-port='output'
                data-block-id={block.blockId}
                data-branch={entry.branch}
                onPointerDown={event => onOutputPointerDown(event, block, entry.branch)}
                sx={portSx(def.color, 'bottom', ratio)}
              />
              <Typography
                variant='caption'
                sx={{
                  position: 'absolute',
                  bottom: -20,
                  left: `${ratio * 100}%`,
                  transform: 'translateX(-50%)',
                  px: 0.5,
                  fontSize: 10,
                  lineHeight: 1.4,
                  borderRadius: 0.5,
                  bgcolor: 'background.paper',
                  color: 'text.secondary',
                  pointerEvents: 'none',
                }}
              >
                {entry.label}
              </Typography>
            </Box>
          );
        })
      ) : (
        <Box
          data-port='output'
          data-block-id={block.blockId}
          onPointerDown={event => onOutputPointerDown(event, block)}
          sx={portSx(def.color, 'bottom')}
        />
      )}
    </Box>
  );
};

export default FlowBlockCard;
